import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

/** Pixel sizes mapped from former Lucide spinner scale. */
const LOADER_SIZE_PX = {
  sm: 16,
  default: 20,
  md: 24,
  lg: 32,
  xl: 50,
} as const

/**
 * Radial-dot + segmented ring loader (`.app-loader` in globals.css).
 * Used by ContentLoader, PageLoader, and inline loading states app-wide.
 */
const spinnerVariants = cva('app-loader shrink-0', {
  variants: {
    size: {
      sm: '',
      default: '',
      md: '',
      lg: '',
      xl: '',
    },
    variant: {
      default: '[--loader-color:hsl(var(--primary))]',
      primary: '[--loader-color:hsl(var(--primary))]',
      secondary: '[--loader-color:hsl(var(--secondary-foreground))]',
      muted: '[--loader-color:hsl(var(--muted-foreground))]',
      white: '[--loader-color:#fff]',
    },
  },
  defaultVariants: {
    size: 'default',
    variant: 'default',
  },
})

export interface SpinnerProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>,
    VariantProps<typeof spinnerVariants> {
  label?: string
  labelPosition?: 'top' | 'bottom' | 'left' | 'right'
}

const Spinner = React.forwardRef<HTMLDivElement, SpinnerProps>(
  ({ className, size, variant, label, labelPosition = 'bottom', style, ...props }, ref) => {
    const resolvedSize = size ?? 'default'
    const loaderStyle = {
      ...style,
      ['--loader-size' as string]: `${LOADER_SIZE_PX[resolvedSize]}px`,
    } as React.CSSProperties

    const icon = (
      <div
        className={cn(spinnerVariants({ size: resolvedSize, variant }), className)}
        style={loaderStyle}
        role="status"
        aria-label={label ?? 'Loading'}
        aria-hidden={!!label}
      />
    )

    if (!label) {
      return (
        <div ref={ref} className="inline-flex items-center justify-center" {...props}>
          {icon}
        </div>
      )
    }

    const labelClasses = cn(
      'text-sm text-muted-foreground',
      labelPosition === 'top' && 'mb-2',
      labelPosition === 'bottom' && 'mt-2',
      labelPosition === 'left' && 'mr-2',
      labelPosition === 'right' && 'ml-2'
    )

    const containerClasses = cn(
      'flex items-center justify-center',
      labelPosition === 'top' || labelPosition === 'bottom' ? 'flex-col' : 'flex-row'
    )

    return (
      <div ref={ref} className={containerClasses} {...props}>
        {labelPosition === 'top' && <span className={labelClasses}>{label}</span>}
        {labelPosition === 'left' && <span className={labelClasses}>{label}</span>}
        {icon}
        {labelPosition === 'right' && <span className={labelClasses}>{label}</span>}
        {labelPosition === 'bottom' && <span className={labelClasses}>{label}</span>}
      </div>
    )
  }
)
Spinner.displayName = 'Spinner'

export { Spinner, spinnerVariants }
